import type { SxProps, Theme } from '@mui/material';

export const learningContentStageClassNames = {
  video: 'skill-video',
  placeholder: 'skill-video-placeholder',
  placeholderButton: 'skill-video-placeholder-button',
  titleCard: 'skill-video-title-card',
  orbit: 'skill-video-orbit',
  link: 'skill-video-link',
  controls: 'skill-video-controls',
  playControl: 'play-control',
  volumeControl: 'volume-control',
  timelineControl: 'timeline-control',
  timeControl: 'time-control',
  screenControl: 'screen-control',
};

const mediaFrameStyles = {
  justifySelf: 'center',
  width: 'min(100%, calc(100cqh * 16 / 9))',
  maxWidth: '100%',
  height: 'auto',
  maxHeight: '100cqh',
  aspectRatio: '16 / 9',
} as const;

export const learningContentStageStyles = {
  video: () =>
    ({
      display: 'block',
      ...mediaFrameStyles,
      borderRadius: '8px',
      background: '#101820',
      objectFit: 'contain',
    }) as SxProps<Theme>,

  placeholder: (theme: Theme) =>
    ({
      position: 'relative',
      overflow: 'hidden',
      display: 'grid',
      alignItems: 'center',
      ...mediaFrameStyles,
      padding: '28px 9%',
      background: '#efc891',
      '&.MuiPaper-root, &.MuiButton-root': {
        background: '#efc891',
      },
      [theme.breakpoints.down('sm')]: {
        padding: '28px 9%',
      },
    }) as SxProps<Theme>,

  placeholderButton: () =>
    ({
      border: 0,
      color: 'inherit',
      font: 'inherit',
      textAlign: 'left',
      cursor: 'pointer',
      textTransform: 'none',
      '&:disabled, &.MuiButton-root.Mui-disabled': {
        color: 'inherit',
        cursor: 'default',
        opacity: 1,
      },
    }) as SxProps<Theme>,

  placeholderButtonFrame: (theme: Theme) =>
    ({
      position: 'relative',
      overflow: 'hidden',
      display: 'grid',
      alignItems: 'center',
      ...mediaFrameStyles,
      padding: '28px 9%',
      background: '#efc891',
      border: 0,
      color: 'inherit',
      font: 'inherit',
      textAlign: 'left',
      cursor: 'pointer',
      textTransform: 'none',
      '&.MuiPaper-root, &.MuiButton-root': {
        background: '#efc891',
      },
      '&:disabled, &.MuiButton-root.Mui-disabled': {
        color: 'inherit',
        cursor: 'default',
        opacity: 1,
      },
      [theme.breakpoints.down('sm')]: {
        padding: '28px 9%',
      },
    }) as SxProps<Theme>,

  titleCard: () =>
    ({
      display: 'grid',
      gap: '8px',
      width: 'min(770px, 85%)',
      color: '#775a38',
    }) as SxProps<Theme>,

  pathLabel: (theme: Theme) =>
    ({
      margin: 0,
      fontSize: '18px',
      fontWeight: 300,
      letterSpacing: 0,
      textTransform: 'uppercase',
      [theme.breakpoints.down('sm')]: {
        fontSize: '18px',
      },
    }) as SxProps<Theme>,

  title: (theme: Theme) =>
    ({
      margin: 0,
      minHeight: '64px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 20px',
      background: '#539c10',
      color: '#ffffff',
      fontSize: '24px',
      fontWeight: 300,
      lineHeight: 1.12,
      [theme.breakpoints.down('sm')]: {
        minHeight: '68px',
        padding: '0 18px',
        fontSize: '24px',
      },
    }) as SxProps<Theme>,

  orbit: (theme: Theme) =>
    ({
      width: '150px',
      height: '150px',
      display: 'grid',
      placeItems: 'center',
      border: '2px solid rgb(94 106 112 / 36%)',
      borderRadius: '999px',
      background: 'linear-gradient(150deg, rgb(255 255 255 / 52%), rgb(94 74 80 / 28%)), #c2d9dd',
      '& span': {
        width: '58px',
        height: '78px',
        borderRadius: '48px 48px 18px 18px',
        background: '#8e64b3',
        boxShadow: '-48px 38px 0 -20px #0e9bb7, 46px 28px 0 -22px #e46c44, 18px -24px 0 -27px #e6902d',
      },
      [theme.breakpoints.down('sm')]: {
        width: '140px',
        height: '140px',
      },
    }) as SxProps<Theme>,

  link: () =>
    ({
      justifySelf: 'start',
      borderRadius: '7px',
      padding: '12px 16px',
      background: '#539c10',
      color: '#ffffff',
      fontWeight: 700,
      textTransform: 'none',
      '&.MuiButton-root:hover': {
        background: '#539c10',
      },
    }) as SxProps<Theme>,

  controls: () =>
    ({
      position: 'absolute',
      right: 0,
      bottom: 0,
      left: 0,
      display: 'grid',
      gridTemplateColumns: '46px 34px minmax(0, 1fr) auto 34px',
      gap: '10px',
      alignItems: 'center',
      height: '32px',
      padding: '0 14px',
      background: '#66676a',
    }) as SxProps<Theme>,

  playControl: () =>
    ({
      width: '36px',
      height: '24px',
      background: '#7f8b96',
      clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
    }) as SxProps<Theme>,

  volumeControl: () =>
    ({
      width: '22px',
      height: '18px',
      background: '#ffffff',
      clipPath: 'polygon(0 35%, 35% 35%, 72% 5%, 72% 95%, 35% 65%, 0 65%)',
    }) as SxProps<Theme>,

  timelineControl: () =>
    ({
      height: '4px',
      background: '#8594a4',
      '& span': {
        display: 'block',
        width: '30%',
        height: '100%',
        background: '#a5b4c0',
      },
    }) as SxProps<Theme>,

  timeControl: () =>
    ({
      color: '#ffffff',
      fontSize: '12px',
      fontWeight: 700,
    }) as SxProps<Theme>,

  screenControl: () =>
    ({
      width: '18px',
      height: '14px',
      border: '3px solid #ffffff',
    }) as SxProps<Theme>,
};
